import * as XLSX from 'xlsx';
import {
  SHIPPING_STATUS,
  SHIPPING_STATUS_LABEL,
  SHIPPING_STATUS_SETTABLE,
  SHIPPING_STATUS_REQUIRE_REASON,
  REASON_MAX_LENGTH,
  BULK_MAX_ITEMS,
  CLEAN_CELL,
  PARSE_SHIPPING_STATUS,
} from './shipping';

// ชื่อคอลัมน์ต้องตรงกับที่ RedeemPointContainer.handleExport เขียนออกไป
export const COLUMN = {
  REF_ID: 'Ref ID',
  LOGIN_ID: 'Login ID',
  REWARD_CODE: 'Coupon code',
  REDEEM_TYPE: 'Redeem type',
  CURRENT_STATUS: 'สถานะการจัดส่ง',
  NEW_STATUS: 'สถานะใหม่',
  REASON: 'เหตุผล',
};

// ถ้าคอลัมน์เหล่านี้หายแปลว่าไม่ใช่ไฟล์ Redeem point หรือถูกแก้หัวตาราง
// ตีกลับทั้งไฟล์ดีกว่าปล่อยให้ไปพังทีละแถวแล้วอ่านไม่ออกว่าเกิดอะไรขึ้น
const REQUIRED_HEADERS = [
  COLUMN.REF_ID,
  COLUMN.LOGIN_ID,
  COLUMN.REWARD_CODE,
  COLUMN.CURRENT_STATUS,
  COLUMN.NEW_STATUS,
];

const VALID_STATUS_TEXT = SHIPPING_STATUS_SETTABLE.map(
  (e) => SHIPPING_STATUS_LABEL[e]
).join(' / ');

// อ่านไฟล์เป็น ArrayBuffer แล้วให้ SheetJS จัดการรูปแบบเอง
const readArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('อ่านไฟล์ไม่สำเร็จ'));
    reader.readAsArrayBuffer(file);
  });

// คืน { fileError } ถ้าไฟล์ใช้ไม่ได้ทั้งไฟล์
// คืน { items, errors, skipped, sheetName, totalRows } ถ้าอ่านได้
export const parseShippingStatusFile = async (file) => {
  if (!file) return { fileError: 'กรุณาเลือกไฟล์' };

  const name = String(file.name || '');
  if (!/\.(xlsx|xls)$/i.test(name)) {
    // ไม่รับ .csv เพราะ Excel บน Windows บันทึกเป็น ANSI/TIS-620 ไม่ใช่ UTF-8
    // ทำให้ป้ายสถานะภาษาไทยเพี้ยนทั้งไฟล์ แล้วทุกแถวจะ error ทั้งที่ผู้ใช้กรอกถูก
    return { fileError: 'รองรับเฉพาะไฟล์ Excel (.xlsx, .xls)' };
  }

  let wb;
  try {
    const buffer = await readArrayBuffer(file);
    wb = XLSX.read(new Uint8Array(buffer), { type: 'array' });
  } catch (e) {
    return {
      fileError:
        'เปิดไฟล์ไม่ได้ กรุณาตรวจสอบว่าไฟล์ไม่เสียหายและไม่ได้ตั้งรหัสผ่าน',
    };
  }

  // ใช้ sheet แรกเท่านั้น ไฟล์ export มี sheet "วิธีใช้" ต่อท้ายอยู่
  const sheetName = wb.SheetNames && wb.SheetNames[0];
  if (!sheetName) return { fileError: 'ไม่พบข้อมูลในไฟล์' };
  const ws = wb.Sheets[sheetName];

  // raw:false กันสถานะที่พิมพ์เป็น 3 กลายเป็น number
  // defval:'' ให้ช่องว่างเป็นสตริงว่าง ไม่ใช่ undefined
  const rows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
  if (!rows || rows.length === 0) return { fileError: 'ไม่พบข้อมูลในไฟล์' };

  const headers = Object.keys(rows[0]);
  const missing = REQUIRED_HEADERS.filter((h) => headers.indexOf(h) === -1);
  if (missing.length > 0) {
    return {
      fileError: `ไฟล์นี้ไม่ใช่ไฟล์ Redeem point หรือหัวคอลัมน์ถูกแก้ไข (ไม่พบคอลัมน์: ${missing.join(
        ', '
      )})`,
    };
  }

  const items = [];
  const errors = [];
  const skipped = [];
  const seen = {};

  rows.forEach((row, index) => {
    // +2 = ข้ามแถวหัวตาราง 1 แถว และ index เริ่มที่ 0
    const rowNo = index + 2;
    const rawNewStatus = CLEAN_CELL(row[COLUMN.NEW_STATUS]);
    const refId = CLEAN_CELL(row[COLUMN.REF_ID]);

    // ไม่ได้กรอกสถานะใหม่ = ไม่ได้ตั้งใจแก้แถวนี้ ข้ามไปเงียบ ๆ
    if (!rawNewStatus) return;

    const push = (message) =>
      errors.push({
        row_no: rowNo,
        id: refId,
        login_id: CLEAN_CELL(row[COLUMN.LOGIN_ID]),
        reason_message: message,
      });

    if (!refId) {
      return push('ไม่มีรหัสอ้างอิง (Ref ID) — แถวนี้ถูกเพิ่มเข้ามาเองหรือไม่?');
    }
    // ต้องเช็ค hex ก่อน ObjectId มีตัวอักษร e ปนได้ตามปกติ
    if (!/^[0-9a-fA-F]{24}$/.test(refId)) {
      if (/^\d+(\.\d+)?e[+-]?\d+$/i.test(refId)) {
        return push(
          'Excel แปลงรหัสอ้างอิงเป็นตัวเลข กรุณา Export ใหม่และอย่าแก้ไขคอลัมน์ Ref ID'
        );
      }
      return push(`รหัสอ้างอิงไม่ถูกต้อง: "${refId}"`);
    }

    const status = PARSE_SHIPPING_STATUS(rawNewStatus);
    if (!status) {
      return push(
        `สถานะ "${rawNewStatus}" ไม่ถูกต้อง — ใช้ได้: ${VALID_STATUS_TEXT}`
      );
    }

    let reason = CLEAN_CELL(row[COLUMN.REASON]);
    if (SHIPPING_STATUS_REQUIRE_REASON.indexOf(status) > -1) {
      if (!reason) {
        return push(
          `สถานะ "${SHIPPING_STATUS_LABEL[status]}" ต้องระบุเหตุผล`
        );
      }
      if (reason.length > REASON_MAX_LENGTH) {
        return push(
          `เหตุผลยาวเกิน ${REASON_MAX_LENGTH} ตัวอักษร (พบ ${reason.length} ตัว)`
        );
      }
    } else {
      // สถานะที่ไม่ต้องใช้เหตุผล ล้างทิ้งให้เลย ผู้ใช้ไม่ต้องลบเอง
      reason = '';
    }

    // e-coupon ไม่มีการจัดส่ง ดักที่นี่ด้วยเพื่อให้ผู้ใช้รู้ก่อนยิง API
    // (backend เช็คซ้ำอยู่แล้ว อันนี้แค่ให้รู้ผลเร็ว)
    const redeemType = CLEAN_CELL(row[COLUMN.REDEEM_TYPE]);
    if (redeemType === 'E-coupon') {
      return push('E-coupon ไม่มีการจัดส่ง ไม่สามารถกำหนดสถานะได้');
    }

    const key = refId.toLowerCase();
    const item = {
      row_no: rowNo,
      id: refId,
      login_id: CLEAN_CELL(row[COLUMN.LOGIN_ID]),
      reward_code: CLEAN_CELL(row[COLUMN.REWARD_CODE]),
      // สถานะเดิมตอน export ใช้ตรวจว่ามีคนแก้แซงหรือไม่
      from_status: PARSE_SHIPPING_STATUS(row[COLUMN.CURRENT_STATUS]) || '',
      shipping_status: status,
      reason: reason,
      // ไว้โชว์ในหน้าพรีวิว
      current_status_text: CLEAN_CELL(row[COLUMN.CURRENT_STATUS]),
      new_status_text: SHIPPING_STATUS_LABEL[status],
      reward_name: CLEAN_CELL(row['Coupon name']),
    };

    if (seen[key]) {
      const prev = seen[key];
      // ซ้ำแล้วค่าเหมือนกัน = ยุบเป็นแถวเดียว ไม่ถือว่าผิด
      if (
        prev.item.shipping_status === item.shipping_status &&
        prev.item.reason === item.reason
      ) {
        skipped.push({
          row_no: rowNo,
          note: `ซ้ำกับแถว ${prev.item.row_no} (ค่าเหมือนกัน)`,
        });
        return;
      }
      // ซ้ำแล้วค่าต่างกัน = เดาไม่ได้ว่าอันไหนถูก ตีกลับทั้งคู่
      const message = `รหัสอ้างอิงซ้ำกันแต่ระบุสถานะไม่ตรงกัน (แถว ${prev.item.row_no} และ ${rowNo})`;
      if (!prev.reported) {
        errors.push({
          row_no: prev.item.row_no,
          id: prev.item.id,
          login_id: prev.item.login_id,
          reason_message: message,
        });
        prev.reported = true;
      }
      return push(message);
    }

    seen[key] = { item, reported: false };
    items.push(item);
  });

  errors.sort((a, b) => a.row_no - b.row_no);

  // ตัดรายการที่ถูกรายงานว่าซ้ำแบบขัดกันออกจากชุดที่จะส่ง
  const conflictIds = errors
    .filter((e) => e.reason_message.indexOf('ซ้ำกันแต่ระบุสถานะ') > -1)
    .map((e) => String(e.id).toLowerCase());
  const cleanItems = items.filter(
    (e) => conflictIds.indexOf(String(e.id).toLowerCase()) === -1
  );

  return {
    items: cleanItems,
    errors,
    skipped,
    sheetName,
    totalRows: rows.length,
  };
};

// ตรวจเงื่อนไขระดับไฟล์หลัง parse เสร็จ คืนข้อความถ้าไปต่อไม่ได้
export const validateParsedResult = ({ items, errors }) => {
  if (errors.length === 0 && items.length === 0) {
    return 'ไม่พบแถวที่ต้องอัปเดต — กรุณากรอกคอลัมน์ "สถานะใหม่" ในแถวที่ต้องการเปลี่ยน';
  }
  if (items.length > BULK_MAX_ITEMS) {
    return `อัปเดตได้ครั้งละไม่เกิน ${BULK_MAX_ITEMS.toLocaleString()} รายการ (พบ ${items.length.toLocaleString()} รายการ)`;
  }
  return '';
};

// สร้างไฟล์รายการที่ผิดให้ผู้ใช้เอาไปแก้
export const downloadErrorReport = (errors) => {
  const data = errors.map((e) => [
    e.row_no,
    e.id || '',
    e.login_id || '',
    e.reason_message,
  ]);
  data.splice(0, 0, ['แถวที่', 'Ref ID', 'Login ID', 'สาเหตุ']);
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 8 }, { wch: 28 }, { wch: 32 }, { wch: 70 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'รายการที่ผิด');
  XLSX.writeFile(wb, 'shipping-status-errors.xlsx');
};

export { SHIPPING_STATUS };
